/**
 * Quick Actions Component - Pre-configured test packets
 */

import React, { useState, useEffect } from 'react';
import { useDiaperStore } from '../../stores/diaperStore';
import { useSimulatorStore } from '../../stores/simulatorStore';
import { useToast } from '../../stores/toastStore';
import { getWetnessPercent, findWetnessForPercent } from '../../lib/utils';
import { useCountdown } from '../../hooks/useCountdown';

export const QuickActions: React.FC = () => {
  const { getDiaperModel } = useDiaperStore();
  const { selectedClip, changeNeededPercent, sendQuickAction, isSendingPacket, currentCriterion } = useSimulatorStore();
  const toast = useToast();
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  
  // Get stabilization period from criterion (default 10 seconds)
  const stabilizationPeriod = currentCriterion?.content?.stabilizationPeriod_sec || 10;
  const { countdowns, startCountdown, clearAllCountdowns } = useCountdown(stabilizationPeriod);

  // Clear countdowns when clip changes
  useEffect(() => {
    clearAllCountdowns();
  }, [selectedClip?.id, clearAllCountdowns]);

  const diaperModel = selectedClip?.diaperModelCode ? getDiaperModel(selectedClip.diaperModelCode) : null;
  const currentWetnessLevel = selectedClip?.wetnessLevel || 0;

  // Calculate wetness level for each action
  const getActionWetnessLevel = (action: string): number => {
    if (!diaperModel) return 0;

    switch (action) {
      case 'OK':
        return 0;
      case 'WET':
        return findWetnessForPercent(10, diaperModel);
      case 'CHANGE_NEEDED':
        if (changeNeededPercent != null) {
          const targetPercent = Math.min(100, changeNeededPercent + 5);
          return findWetnessForPercent(targetPercent, diaperModel);
        }
        return 0;
      case 'LEAK_IMMINENT':
        if (diaperModel.leakThreshold?.wetnessLevel) {
          // Use the leak threshold from diaper model + a small increment
          // Don't cap at 100% - leak threshold is the target
          return Math.min(32767, diaperModel.leakThreshold.wetnessLevel + 100);
        }
        return 0;
      default:
        return 0;
    }
  };

  const getActionLabel = (action: string): string => {
    if (!diaperModel) return action;

    switch (action) {
      case 'OK':
        return 'OK (0%)';
      case 'WET': {
        // Always send 10% wetness
        return `Wet Diaper (10%)`;
      }
      case 'CHANGE_NEEDED': {
        if (changeNeededPercent != null) {
          // Send threshold + 5%
          const targetPercent = Math.min(100, changeNeededPercent + 5);
          return `Change Needed (${targetPercent.toFixed(0)}%)`;
        }
        return 'Change Needed (—)';
      }
      case 'LEAK_IMMINENT': {
        if (diaperModel.leakThreshold?.wetnessLevel) {
          // Show the percentage for leak threshold + increment
          const targetWetness = Math.min(32767, diaperModel.leakThreshold.wetnessLevel + 100);
          const targetPercent = getWetnessPercent(targetWetness, diaperModel);
          if (targetPercent != null) {
            return `Leak Imminent (${Math.round(targetPercent)}%)`;
          }
        }
        return 'Leak Imminent';
      }
      default:
        return action;
    }
  };

  const handleQuickAction = async (action: string) => {
    if (!selectedClip) {
      toast.error('Please select a session first');
      return;
    }

    if (action === 'CHANGE_NEEDED' && changeNeededPercent == null) {
      toast.error('Change Needed threshold not loaded yet. Please select a session and wait for criterion to load.');
      return;
    }

    setLoadingAction(action);
    try {
      await sendQuickAction(action, diaperModel);
      toast.success(`Packet sent: ${getActionLabel(action)}`);
      
      // Start countdown for wetness-related actions
      if (['WET', 'CHANGE_NEEDED', 'LEAK_IMMINENT'].includes(action)) {
        startCountdown(action);
      }
    } catch (error: any) {
      toast.error(`Failed to send packet: ${error.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const isDisabled = (action: string) => {
    if (!selectedClip || isSendingPacket) return true;
    if (action === 'CHANGE_NEEDED' && changeNeededPercent == null) return true;
    
    // Check if action would send a lower wetness level than current
    // (only for wetness-related actions)
    if (['OK', 'WET', 'CHANGE_NEEDED', 'LEAK_IMMINENT'].includes(action)) {
      const actionWetnessLevel = getActionWetnessLevel(action);
      if (actionWetnessLevel < currentWetnessLevel) {
        return true;
      }
    }
    
    return false;
  };

  const getDisabledReason = (action: string): string | null => {
    if (!selectedClip) return null;
    if (action === 'CHANGE_NEEDED' && changeNeededPercent == null) {
      return 'Load criterion to enable';
    }
    
    // Check if action would send a lower wetness level than current
    if (['OK', 'WET', 'CHANGE_NEEDED', 'LEAK_IMMINENT'].includes(action)) {
      const actionWetnessLevel = getActionWetnessLevel(action);
      if (actionWetnessLevel < currentWetnessLevel) {
        const currentPercent = getWetnessPercent(currentWetnessLevel, diaperModel);
        return `Cannot send lower wetness than current (${currentWetnessLevel} / ${currentPercent?.toFixed(1)}%). Attach new diaper to reset.`;
      }
    }
    
    return null;
  };

  const renderCountdownBadge = (action: string) => {
    const countdown = countdowns[action];
    if (!countdown) return null;

    if (countdown.isReady) {
      return (
        <span className="ml-2 px-2 py-1 text-xs font-semibold bg-green-600 text-white rounded-full animate-pulse">
          ✅ Ready
        </span>
      );
    }

    return (
      <span className="ml-2 px-2 py-1 text-xs font-semibold bg-yellow-500 text-white rounded-full">
        ⏱️ {countdown.remainingSeconds}s
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-bold mb-4">Quick Actions</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <div className="relative group">
          <button
            onClick={() => handleQuickAction('OK')}
            disabled={isDisabled('OK')}
            className="w-full py-4 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {loadingAction === 'OK' ? (
              '⏳ Sending...'
            ) : (
              <>
                <img src="/icons/OK.png" alt="OK" className="w-8 h-8 brightness-0 invert" />
                {getActionLabel('OK')}
              </>
            )}
          </button>
          {isDisabled('OK') && getDisabledReason('OK') && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              {getDisabledReason('OK')}
            </div>
          )}
        </div>

        <div className="relative group">
          <button
            onClick={() => handleQuickAction('WET')}
            disabled={isDisabled('WET')}
            className="w-full py-4 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {loadingAction === 'WET' ? (
              '⏳ Sending...'
            ) : (
              <>
                <img src="/icons/OK.png" alt="Wet" className="w-8 h-8 brightness-0 invert" />
                <span>{getActionLabel('WET')}</span>
              </>
            )}
            {renderCountdownBadge('WET')}
          </button>
          {isDisabled('WET') && getDisabledReason('WET') && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              {getDisabledReason('WET')}
            </div>
          )}
        </div>

        <div className="relative group">
          <button
            onClick={() => handleQuickAction('CHANGE_NEEDED')}
            disabled={isDisabled('CHANGE_NEEDED')}
            className="w-full py-4 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {loadingAction === 'CHANGE_NEEDED' ? (
              '⏳ Sending...'
            ) : (
              <>
                <img src="/icons/change_needed.png" alt="Change Needed" className="w-8 h-8 brightness-0 invert" />
                <span>{getActionLabel('CHANGE_NEEDED')}</span>
              </>
            )}
            {renderCountdownBadge('CHANGE_NEEDED')}
          </button>
          {isDisabled('CHANGE_NEEDED') && getDisabledReason('CHANGE_NEEDED') && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              {getDisabledReason('CHANGE_NEEDED')}
            </div>
          )}
        </div>

        <div className="relative group">
          <button
            onClick={() => handleQuickAction('LEAK_IMMINENT')}
            disabled={isDisabled('LEAK_IMMINENT')}
            className="w-full py-4 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {loadingAction === 'LEAK_IMMINENT' ? (
              '⏳ Sending...'
            ) : (
              <>
                <img src="/icons/leak.png" alt="Leak" className="w-8 h-8 brightness-0 invert" />
                <span>{getActionLabel('LEAK_IMMINENT')}</span>
              </>
            )}
            {renderCountdownBadge('LEAK_IMMINENT')}
          </button>
          {isDisabled('LEAK_IMMINENT') && getDisabledReason('LEAK_IMMINENT') && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              {getDisabledReason('LEAK_IMMINENT')}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <button
          onClick={() => handleQuickAction('DETACHED')}
          disabled={isDisabled('DETACHED')}
          className="py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {loadingAction === 'DETACHED' ? (
            '⏳ Sending...'
          ) : (
            <>
              <img src="/icons/detached.png" alt="Detached" className="w-8 h-8 brightness-0 invert" />
              Clip Detached
            </>
          )}
        </button>

        <button
          onClick={() => handleQuickAction('LOW_BATTERY')}
          disabled={isDisabled('LOW_BATTERY')}
          className="py-4 bg-yellow-600 text-white font-semibold rounded-lg hover:bg-yellow-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {loadingAction === 'LOW_BATTERY' ? (
            '⏳ Sending...'
          ) : (
            <>
              <img src="/icons/low_battery.png" alt="Low Battery" className="w-8 h-8 brightness-0 invert" />
              Low Battery
            </>
          )}
        </button>

        <button
          onClick={() => handleQuickAction('INVALID_DIAPER')}
          disabled={isDisabled('INVALID_DIAPER')}
          className="py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {loadingAction === 'INVALID_DIAPER' ? (
            '⏳ Sending...'
          ) : (
            <>
              <img src="/icons/unknow_diaper.png" alt="Invalid Diaper" className="w-8 h-8 brightness-0 invert" />
              Invalid Diaper
            </>
          )}
        </button>

        <button
          onClick={() => handleQuickAction('SHORTED')}
          disabled={isDisabled('SHORTED')}
          className="py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {loadingAction === 'SHORTED' ? (
            '⏳ Sending...'
          ) : (
            <>
              <img src="/icons/clip_problem.png" alt="Shorted" className="w-8 h-8 brightness-0 invert" />
              Clip Shorted
            </>
          )}
        </button>
      </div>
    </div>
  );
};

